import Header from "@/components/bionexus/Header";
import Footer from "@/components/Footer";
import BackNav from "@/components/BackNav";
import { useParams } from "react-router-dom";
import { Link } from "@/components/LocaleLink";
import { useI18n } from "@/i18n/I18nProvider";
import { applicationTypeLabel, italianName, localizedName, translatedList, translatedText } from "@/i18n/content";
import SeoAlternates from "@/components/SeoAlternates";
import { applicationJsonLd } from "@/lib/structuredData";
import { useApplication, useApplicationMaterials, useApplications } from "@/hooks/useBioNexusData";
import { ExternalLink, Building2, Leaf, ArrowUpRight } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Reveal from "@/components/Reveal";
import DetailHero from "@/components/detail/DetailHero";
import { DetailSection, Eyebrow, OnThisPage, ReadingProgress, SectionLink } from "@/components/detail/DetailLayout";
import { KeyFacts, SpotlightCard, TRLMeter } from "@/components/detail/DataDisplay";
import { splitImageNote, splitLead } from "@/lib/text";


const initials = (name: string) =>
  name
    .replace(/\(.*?\)/g, "")
    .split(/\s+/)
    .filter((w) => /^[A-Za-z]/.test(w))
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "·";

const ApplicationDetail = () => {
  const { id } = useParams();
  const { data: application, isLoading } = useApplication(id);
  const { data: materials } = useApplicationMaterials(id);
  const { data: allApplications } = useApplications();
  const { m, locale, formatDate } = useI18n();
  const S = m.application.sections;
  const relTitle = (a: { title: string; title_cn?: string | null }) => localizedName(locale, a.title, a.title_cn);
  const matName = (x?: { name?: string | null; name_cn?: string | null } | null) => localizedName(locale, x?.name || "", x?.name_cn);

  const relatedApps = allApplications?.filter(a => a.id !== id).slice(0, 3) || [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20">
          <div className="h-[60vh] bg-muted animate-pulse" />
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
            <div className="h-12 bg-muted w-1/2 mb-4 animate-pulse" />
            <div className="h-6 bg-muted w-3/4 animate-pulse" />
          </div>
        </main>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-32 text-center px-6">
          <h1 className="font-display font-medium text-3xl mb-4">{m.application.notFound}</h1>
          <Link to="/applications" className="text-primary hover:underline">
            ← {m.common.backApplications}
          </Link>
        </main>
      </div>
    );
  }

  const app = application as any; // Type assertion for new fields
  const title = localizedName(locale, application.title, app.title_cn, italianName(application, "title"));
  const desc = translatedText(application, "description", application.description, locale);
  const technical = translatedText(application, "technical_brief", app.technical_brief, locale);
  const impact = translatedText(application, "impact_brief", app.impact_brief, locale);
  const featureList = translatedList(application, "key_features", app.key_features, locale);
  const { body: descriptionBody, note: imageNote } = splitImageNote(desc.text || "");
  const { lead, rest } = splitLead(descriptionBody);
  const features: string[] = featureList.items;
  const technologies: string[] = app.technologies || [];
  const creators: string[] = app.creators || [];
  const sourceHost = (() => {
    try {
      return new URL(application.source_url || "").hostname.replace(/^www\./, "");
    } catch {
      return application.source_url;
    }
  })();

  const sections: SectionLink[] = [
    { id: "overview", title: S.overview },
    features.length > 0 && { id: "features", title: S.features },
    (app.technical_brief || technologies.length > 0) && { id: "technology", title: S.technology },
    app.impact_brief && { id: "impact", title: S.impact },
    (app.organization || creators.length > 0) && { id: "team", title: S.team },
    materials && materials.length > 0 && { id: "materials", title: S.materials },
  ].filter(Boolean) as SectionLink[];

  return (
    <div className="min-h-screen bg-background page-enter">
      <Helmet>
        <title>{`${title.primary} — BioNexus`}</title>
        <meta name="description" content={m.meta.applicationDescription(title.primary)} />
        <meta property="og:title" content={`${title.primary} — BioNexus`} />
        <meta property="og:description" content={m.meta.applicationDescription(title.primary)} />
        <meta property="og:type" content="article" />
        {application.image_url && <meta property="og:image" content={application.image_url.startsWith("http") ? application.image_url : `https://bionexus.news${application.image_url}`} />}
        <script type="application/ld+json">
          {JSON.stringify(applicationJsonLd(application, locale, { title: title.primary, description: splitImageNote(desc.text || "").body }))}
        </script>
      </Helmet>
      <SeoAlternates path={`/applications/${application.id}`} />
      <Header />
      <ReadingProgress />

      <main className="pt-20">
        <DetailHero
          image={application.image_url}
          imageAlt={title.primary}
          credit={application.image_credit}
          creditUrl={application.image_source_url || application.source_url}
          eyebrow={
            <>
              <span className="rounded-full bg-primary/15 border border-primary/40 px-3 py-1 font-mono text-xs uppercase text-primary backdrop-blur-sm">
                {applicationTypeLabel(m, application.application_type)}
              </span>
              {application.category && <span lang="en" className="font-mono text-xs text-white/70">{application.category}</span>}
            </>
          }
          title={title.primary}
          titleLang={title.primaryLang}
          subtitle={title.secondary && <span lang={title.secondaryLang}>{title.secondary}</span>}
        />

        <KeyFacts
          facts={[
            { label: m.application.facts.type, value: applicationTypeLabel(m, application.application_type) },
            { label: m.application.facts.category, value: application.category && <span lang="en">{application.category}</span> },
            { label: m.application.facts.year, value: app.year },
            { label: m.application.facts.trl, node: app.trl_level ? <TRLMeter level={app.trl_level} compact /> : null },
          ]}
        />

        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-12 md:pt-16">
          <div className="mb-12 flex flex-wrap items-baseline justify-between gap-4">
            <BackNav to="/applications" label={m.common.backApplications} />
            {m.common.contentLanguageNote && !desc.translated && <p className="text-xs text-muted-foreground">{m.common.contentLanguageNote}</p>}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-12 xl:gap-20">
            <article className="lg:col-span-8 space-y-20 md:space-y-24 min-w-0">
              <DetailSection id="overview" title={S.overview}>
                <div className="max-w-[68ch]">
                  <p lang={lead ? desc.lang : undefined} className="font-display font-normal text-[1.8rem] md:text-[2rem] leading-[1.45] text-balance">
                    {lead || m.application.noDescription}
                  </p>
                  {rest && <p lang={desc.lang} className="mt-6 text-muted-foreground leading-[1.8]">{rest}</p>}
                  {imageNote && <p lang="en" className="mt-6 font-mono text-xs text-muted-foreground/70">{imageNote}</p>}
                </div>
              </DetailSection>

              {features.length > 0 && (
                <DetailSection id="features" title={S.features}>
                  <ol className="grid grid-cols-1 sm:grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border">
                    {features.map((feature: string, idx: number) => (
                      <li
                        key={idx}
                        className={`group bg-background p-6 transition-colors duration-300 hover:bg-primary/[0.04] ${
                          features.length % 2 === 1 && idx === features.length - 1 ? "sm:col-span-2" : ""
                        }`}
                      >
                        <span className="font-mono text-xs text-primary tabular-nums">{String(idx + 1).padStart(2, "0")}</span>
                        <p lang={featureList.lang} className="mt-3 leading-relaxed transition-transform duration-500 ease-expo-out group-hover:translate-x-1">
                          {feature}
                        </p>
                      </li>
                    ))}
                  </ol>
                </DetailSection>
              )}

              {(app.technical_brief || technologies.length > 0) && (
                <DetailSection id="technology" title={S.technology}>
                  <div className="max-w-[68ch]">
                    {technical.text && <p lang={technical.lang} className="leading-[1.8]">{technical.text}</p>}
                    {technologies.length > 0 && (
                      <div className={app.technical_brief ? "mt-8" : ""}>
                        <Eyebrow className="mb-4">{m.application.technologies}</Eyebrow>
                        <div className="flex flex-wrap gap-2">
                          {technologies.map((tech: string, idx: number) => (
                            <span
                              key={idx}
                              className="font-mono text-xs rounded-full border border-border px-3 py-1.5 transition-colors hover:border-primary/60 hover:text-primary"
                            >
                              <span lang="en">{tech}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </DetailSection>
              )}

              {app.impact_brief && (
                <DetailSection id="impact" title={S.impact}>
                  <div className="relative overflow-hidden rounded-lg border border-primary/25 bg-gradient-to-br from-primary/[0.10] via-primary/[0.03] to-transparent p-8 md:p-10">
                    <Leaf aria-hidden className="leaf-sway absolute -right-6 -bottom-6 h-40 w-40 text-primary/10" strokeWidth={1} />
                    <Leaf className="h-5 w-5 text-primary mb-5" />
                    <p lang={impact.lang} className="relative max-w-[60ch] text-lg leading-[1.75]">{impact.text}</p>
                  </div>
                </DetailSection>
              )}

              {(app.organization || creators.length > 0) && (
                <DetailSection id="team" title={S.team}>
                  {app.organization && (
                    <div className="flex items-start gap-4 mb-8">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                        <Building2 className="h-5 w-5" />
                      </span>
                      <div>
                        <Eyebrow className="mb-1">{m.application.leadOrganization}</Eyebrow>
                        <p lang="en" className="font-display font-medium text-lg leading-snug">{app.organization}</p>
                      </div>
                    </div>
                  )}
                  {creators.length > 0 && (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {creators.map((creator: string, idx: number) => (
                        <li key={idx} className="flex items-center gap-3 rounded-lg border border-border px-4 py-3 transition-colors hover:border-primary/40">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-[11px] text-muted-foreground">
                            {initials(creator)}
                          </span>
                          <span lang="en" className="text-sm">{creator}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </DetailSection>
              )}

              {materials && materials.length > 0 && (
                <DetailSection id="materials" title={S.materials}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {materials.map((item: any) => (
                      <Link key={item.material_id} to={`/materials/${item.material_id}`} className="group block">
                        <SpotlightCard className="flex items-center gap-4 p-4">
                          {item.materials?.image_url ? (
                            <img
                              src={item.materials.image_url}
                              alt={item.materials?.name}
                              loading="lazy"
                              className="h-16 w-16 shrink-0 rounded-md object-cover"
                            />
                          ) : (
                            <span className="h-16 w-16 shrink-0 rounded-md bg-gradient-to-br from-primary/20 to-muted" />
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="font-display font-medium leading-snug group-hover:text-primary transition-colors">
                              <span lang={matName(item.materials).primaryLang}>{matName(item.materials).primary}</span>
                            </p>
                            {item.materials?.family && (
                              <p lang="en" className="font-mono text-xs text-muted-foreground mt-1">{item.materials.family}</p>
                            )}
                          </div>
                          <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-primary" />
                        </SpotlightCard>
                      </Link>
                    ))}
                  </div>
                </DetailSection>
              )}
            </article>

            <aside className="lg:col-span-4 lg:sticky lg:top-32 lg:self-start space-y-10">
              <OnThisPage sections={sections} />

              {application.source_url && (
                <a
                  href={application.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between gap-4 rounded-lg border border-primary/30 bg-gradient-to-br from-primary/[0.08] to-transparent p-6 transition-colors hover:border-primary/60"
                >
                  <div className="min-w-0">
                    <Eyebrow className="text-primary mb-2">{m.application.projectSource}</Eyebrow>
                    <p className="font-mono text-sm truncate">{sourceHost}</p>
                  </div>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform duration-300 group-hover:rotate-45">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </a>
              )}

              {app.tags && app.tags.length > 0 && (
                <div>
                  <Eyebrow className="mb-4">{m.application.tags}</Eyebrow>
                  <div className="flex flex-wrap gap-2">
                    {app.tags.map((tag: string, idx: number) => (
                      <span key={idx} className="rounded-full bg-primary/10 px-3 py-1 font-mono text-xs text-primary">
                        <span lang="en">#{tag}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <p className="font-mono text-xs text-muted-foreground/70">{m.common.lastUpdated(formatDate(application.updated_at))}</p>
            </aside>
          </div>

          {relatedApps.length > 0 && (
            <section className="mt-28 pt-12 border-t border-border">
              <div className="flex justify-between items-baseline mb-10">
                <h2 className="font-display font-medium text-2xl md:text-3xl tracking-tight">{m.application.more}</h2>
                <Link to="/applications" className="group text-sm font-medium text-primary">
                  {m.common.viewAll} <span className="arrow-nudge">→</span>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedApps.map((relApp, i) => (
                  <Reveal key={relApp.id} delay={i * 80}>
                    <Link to={`/applications/${relApp.id}`} className="group block">
                      <div className="aspect-video bg-muted mb-4 overflow-hidden rounded-lg">
                        {relApp.image_url ? (
                          <img
                            src={relApp.image_url}
                            alt={relApp.title}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-700 ease-expo-out group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-primary/15 to-muted" />
                        )}
                      </div>
                      <span className="font-mono text-xs uppercase text-primary">{applicationTypeLabel(m, relApp.application_type)}</span>
                      <h3 className="font-display font-medium text-lg mt-1 leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                        <span lang={relTitle(relApp).primaryLang}>{relTitle(relApp).primary}</span>
                      </h3>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ApplicationDetail;
